import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LmsStudentType } from '../../../constants/types/lms-type';
import { StudentProfileForm } from '../../../constants/types/student-profile-form';
import TextWinds from '../../../components/common/TextWinds';
import HFbutton from '../../../components/common/input/HFbutton';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  getStudentDetail,
  pickChangedStudentFields,
  updateStudentDetail,
  toStudentProfileForm,
} from '../../../api/utils/lms/student/api';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import {
  SECTION_FIELDS,
  SectionKey,
} from '../../../components/lms/student/detail/constants';
import BasicInfoSection from '../../../components/student/profile/BasicInfoSection';
import ExtraInfoSection from '../../../components/lms/student/detail/ExtraInfoSection';
import ParentsSection from '../../../components/student/profile/ParentsSection';
import { useSectionEditForm } from '../../../hooks/student/useSectionEditForm';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

/** LMS > 학생 관리 > 학생 상세 정보 화면 */
const StudentDetail = () => {
  const [oldStudentData, setOldStudentData] = useState<LmsStudentType>();
  const [loadFailed, setLoadFailed] = useState(false);
  const { classId, studentId } = useParams<{
    classId: string;
    studentId: string;
  }>();
  const {
    setValue,
    getValues,
    trigger,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<StudentProfileForm>({
    defaultValues: {
      parents: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parents',
  });

  const init = () => {
    setLoadFailed(false);
    getStudentDetail(classId, Number(studentId))
      .then((data) => {
        setOldStudentData(data);
        reset(toStudentProfileForm(data));
      })
      .catch((err) => {
        console.error(err);
        setLoadFailed(true);
      });
  };

  useEffect(() => {
    init();
  }, [classId, studentId]);

  const editForm = useSectionEditForm({
    fields: SECTION_FIELDS,
    form: { getValues, setValue, trigger, watch, reset },
    getOrigin: () => oldStudentData,
    save: async (section) => {
      const params = pickChangedStudentFields(
        getValues(),
        oldStudentData,
        SECTION_FIELDS[section],
      );
      if (Object.keys(params).length === 0) return false;
      await updateStudentDetail(classId, Number(studentId), params);
      return true;
    },
    onSaved: init,
  });

  /** 영역 헤더에 넘길 공통 props */
  const sectionProps = (section: SectionKey) => ({
    isEditing: editForm.editSection === section,
    disabled: editForm.editSection !== null,
    isSaving: editForm.editSection === section && editForm.isSaving,
    onEdit: () => editForm.startEdit(section),
    onCancel: () => editForm.cancel(section),
    onSave: () => editForm.submit(section),
  });

  // 조회 실패 / 조회 중에는 폼 대신 상태만 보여준다.
  if (loadFailed || !oldStudentData) {
    return (
      <div className="flex w-full flex-col pt-4 xl:px-5 xl:pt-6">
        <StudentDetailHeader />
        <div className="my-5 bg-white p-4 xl:rounded-lg xl:shadow-card">
          {loadFailed ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-4">
              <TextWinds type="content_body2" color="gray7">
                학생 정보를 불러오지 못했습니다.
              </TextWinds>
              <div className="w-[160px]">
                <HFbutton height={36} onClick={init}>
                  다시 시도
                </HFbutton>
              </div>
            </div>
          ) : (
            <LoadingSpinner message="데이터 조회중" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col pt-4 xl:px-5 xl:pt-6">
      <StudentDetailHeader />

      <div className="my-5 flex flex-col gap-x-1 bg-white p-4 xl:rounded-lg xl:p-4 xl:shadow-card">
        <form>
          <BasicInfoSection
            control={control}
            errors={errors}
            section={sectionProps('basic')}
            uid={oldStudentData.uid}
            name={oldStudentData.name}
          />

          <hr className="my-6 border-gray2" />

          <ExtraInfoSection
            control={control}
            errors={errors}
            section={sectionProps('extra')}
            setValue={setValue}
          />

          <hr className="my-6 border-gray2" />

          <ParentsSection
            control={control}
            errors={errors}
            section={sectionProps('parents')}
            fields={fields}
            append={append}
            remove={remove}
            className="mt-6"
            // INFO: API는 여러 명을 지원하지만, 학생 화면과 정책을 맞추기 위해 화면에서 1명으로 제한합니다.
            maxParents={1}
          />
        </form>
      </div>
    </div>
  );
};

/** 학생 상세 정보 페이지 헤더 */
const StudentDetailHeader = () => {
  return (
    <header className="flex flex-col gap-3 px-5 xl:px-0">
      <Link
        to=".."
        relative="path"
        className="flex w-fit items-center gap-1 text-gray7 hover:text-purple5"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        <TextWinds type="content_body3">학생 관리 목록</TextWinds>
      </Link>
      <div className="min-w-0">
        <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
          학생 상세 정보
        </TextWinds>

        <TextWinds type="content_body3">
          학생의 상세 정보를 확인하고 수정할 수 있어요.
        </TextWinds>
      </div>
    </header>
  );
};

export default StudentDetail;
